// @flow

import _ from 'lodash';
import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';
import { trackFSEvent } from 'src/integrations/fullStory';

import apiService from 'src/services/api-service';
import {
  VERIFY_DISCOUNT_CODE_REQUEST,
  VERIFY_DISCOUNT_CODE_SUCCESS,
  VERIFY_DISCOUNT_CODE_FAILURE,
  SUBSCRIBE_PLAN_REQUEST,
  SUBSCRIBE_PLAN_SUCCESS,
  SUBSCRIBE_PLAN_FAILURE,
  ACTIVATE_CHARGE_REQUEST,
  ACTIVATE_CHARGE_SUCCESS,
  ACTIVATE_CHARGE_FAILURE,
  CLEAR_DISCOUNT_CODE,
  GET_GMV_LIMIT_REQUEST,
  GET_GMV_LIMIT_SUCCESS,
  GET_GMV_LIMIT_FAILURE,
  VALIDATE_GMV_LIMIT_REQUEST,
  VALIDATE_GMV_LIMIT_SUCCESS,
  VALIDATE_GMV_LIMIT_FAILURE,
  GET_GMV_LIMIT_PLANS_REQUEST,
  GET_GMV_LIMIT_PLANS_SUCCESS,
  GET_GMV_LIMIT_PLANS_FAILURE,
  GET_GMV_BILLING_HISTORY_REQUEST,
  GET_GMV_BILLING_HISTORY_SUCCESS,
  GET_GMV_BILLING_HISTORY_FAILURE,
  GET_GMV_LIMIT_PLANS_BY_TYPE_FAILURE,
  GET_GMV_LIMIT_PLANS_BY_TYPE_REQUEST,
  GET_GMV_LIMIT_PLANS_BY_TYPE_SUCCESS,
  GET_INVOICE_PLAN_REQUEST,
  GET_INVOICE_PLAN_SUCCESS,
  GET_INVOICE_PLAN_FAILURE,
  STORE_CURRENT_ROUTE_SUCCESS,
  EXPORT_GMV_BILLING_HISTORY_REQUEST,
  EXPORT_GMV_BILLING_HISTORY_SUCCESS,
  EXPORT_GMV_BILLING_HISTORY_FAILURE
} from 'stores/flow-type/subscription-v5.type';

/**
 * call to verify discount code
 * @return {object} The RSAA object for API middleware
 */
export const verifyDiscountCode = (code, newPlanName) => {
  return apiService.sendRequest(
    'admin-v3.subscription-v5.verify-discount',
    { body: { code, newPlanName } },
    [
      VERIFY_DISCOUNT_CODE_REQUEST,
      {
        type: VERIFY_DISCOUNT_CODE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data: data?.data };
        }
      },
      {
        type: VERIFY_DISCOUNT_CODE_FAILURE,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      }
    ]
  );
};

/**
 * call to subscribe a plan
 * @return {object} The RSAA object for API middleware
 */
export const subscribe = (planId: string, code: string, host, i18n: I18n) => {
  const params = { planId };
  // Prepare Body
  let dataBody = { host };
  if (code) {
    dataBody = _.assign(dataBody, {
      discountCode: code
    });
  }

  return apiService.sendRequest('admin-v3.subscription-v5.subscribe', { params, body: dataBody }, [
    SUBSCRIBE_PLAN_REQUEST,
    {
      type: SUBSCRIBE_PLAN_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: SUBSCRIBE_PLAN_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV5.NotifyText.subscribe_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to activate charge
 * @return {object} The RSAA object for API middleware
 */
export const activate = (chargeId: string, isOnboardingFlow: Boolean, i18n: I18n) => {
  const params = { chargeId };
  const body = { isOnboardingFlow: !!isOnboardingFlow };

  return apiService.sendRequest('admin-v3.subscription-v5.activate', { params, body }, [
    ACTIVATE_CHARGE_REQUEST,
    {
      type: ACTIVATE_CHARGE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: ACTIVATE_CHARGE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV5.NotifyText.activate_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * Reset setup feedback state
 * @return {object} The RSAA object for API middleware
 */
export const clearDiscountCode = () => {
  return {
    type: CLEAR_DISCOUNT_CODE,
    payload: {}
  };
};

/**
 * Store current route for back
 * @return {object} The RSAA object for API middleware
 */
export const storeBackRoute = route => {
  return {
    type: STORE_CURRENT_ROUTE_SUCCESS,
    payload: { route }
  };
};

/**
 * Get current base gmv limit of store ( return 3 months by default)
 * @param {*} i18n
 * @returns
 */
export const getBaseGmvLimit = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.subscription-v5.get-current-base-gmv', {}, [
    GET_GMV_LIMIT_REQUEST,
    {
      type: GET_GMV_LIMIT_SUCCESS,
      payload: async (action, state, res) => {
        const { data } = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_GMV_LIMIT_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV5.NotifyText.getCurrentBaseGMV_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * Get current base gmv limit of store ( return 3 months by default)
 * @param {*} i18n
 * @returns
 */
export const validateBaseGmvLimit = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.subscription-v5.get-current-base-gmv', {}, [
    VALIDATE_GMV_LIMIT_REQUEST,
    {
      type: VALIDATE_GMV_LIMIT_SUCCESS,
      payload: async (action, state, res) => {
        const { data } = await getJSON(res);

        return { data };
      }
    },
    {
      type: VALIDATE_GMV_LIMIT_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV5.NotifyText.getCurrentBaseGMV_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * Get list of plans store can subscribe
 * @param {*} i18n
 * @returns
 */
export const getAllGMVPlans = (type = 'normal', i18n: I18n) => {
  return apiService.sendRequest('admin-v3.subscription-v5.get-gmv-plans', { queries: { type } }, [
    GET_GMV_LIMIT_PLANS_REQUEST,
    {
      type: GET_GMV_LIMIT_PLANS_SUCCESS,
      payload: async (action, state, res) => {
        const { data } = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_GMV_LIMIT_PLANS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV5.NotifyText.getListGMVPlan_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * Get list of plans store can subscribe
 * @param {{fromDate: number, toDate: number, skip: number}} queries
 * @param {I18n} i18n
 * @returns
 */
export const getGMVBillingHistory = (queries = {}, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.subscription-v5.get-gmv-billing-history', { queries }, [
    GET_GMV_BILLING_HISTORY_REQUEST,
    {
      type: GET_GMV_BILLING_HISTORY_SUCCESS,
      payload: async (action, state, res) => {
        const { data } = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_GMV_BILLING_HISTORY_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV5.NotifyText.getListGMVPlan_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * Get list of plans store can subscribe
 * @param {*} i18n
 * @param {string} planId
 * @returns
 */
export const getGMVPlansByType = (planId: string, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.subscription-v5.get-gmv-plan-by-id',
    { params: { id: planId } },
    [
      GET_GMV_LIMIT_PLANS_BY_TYPE_REQUEST,
      {
        type: GET_GMV_LIMIT_PLANS_BY_TYPE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_GMV_LIMIT_PLANS_BY_TYPE_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.SubscriptionV5.NotifyText.getListGMVPlan_Failure'),
            i18n
          )
      }
    ]
  );
};

/**
 * Get detail invoice for plan of store
 * @param {*} i18n
 * @returns
 */
export const getDetailInvoice = i18n => {
  return apiService.sendRequest('admin-v3.subscription-v5.get-invoice', {}, [
    GET_INVOICE_PLAN_REQUEST,
    {
      type: GET_INVOICE_PLAN_SUCCESS,
      payload: async (action, state, res) => {
        const { data } = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_INVOICE_PLAN_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV5.NotifyText.getInvoice_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to export gmv billing history
 * @return {object} The RSAA object for API middleware
 */
export const exportFile = (
  preferDate: string,
  numberOfMonths: number = 3,
  billingHistoryId: string,
  i18n: I18n
) => {
  return apiService.sendRequest(
    'admin-v3.subscription-v5.export-gmv-billing-history',
    { queries: { preferDate, numberOfMonths, billingHistoryId } },
    [
      EXPORT_GMV_BILLING_HISTORY_REQUEST,
      {
        type: EXPORT_GMV_BILLING_HISTORY_SUCCESS,
        payload: async (action, state, res) => {
          // Convert response data into blob and download

          const data = await processRes(
            res,
            'custom',
            i18n.translate('AdminHTML.SubscriptionV5.NotifyText.exportFile_Success'),
            i18n
          );

          // Track Full Story event
          trackFSEvent('Export GMV Billing History', {
            feature: 'Subscription-v5'
          });

          return { data };
        }
      },
      {
        type: EXPORT_GMV_BILLING_HISTORY_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.SubscriptionV5.NotifyText.exportFile'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};
